import {
    MediaPauseEvent,
    MediaPlayer,
    MediaPlayerInstance,
    MediaPlayEvent,
    MediaPlayRequestEvent,
    MediaProvider,
    MediaSeekedEvent,
    TimeSliderInstance,
    useMediaRemote,
    useMediaStore,
} from '@vidstack/react';
import { PlyrLayout, plyrLayoutIcons } from '@vidstack/react/player/layouts/plyr';
import { faCompress, faExpand } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useEffect, useRef } from 'react';
import { useVideoPlayerSync } from '$State/VideoPlayerSync/VideoPlayerSyncContext';
import { Box } from 'MaterialUIComponents';

import { FileUploadTypeEnum } from '$Generated/api';
import VideoTimeSliderControl from './Controls/VideoTimeSliderControl';
import VideoDownloadControl from './Controls/VideoDownloadControl';
import SmallViewPlayOverlay from './Controls/SmallViewPlayOverlay';
import { React } from 'Imports';

import '@vidstack/react/player/styles/base.css';
import '@vidstack/react/player/styles/plyr/theme.css';

interface VideoPlayerProps {
    src: string;
    title?: string;
    maxHeight?: string | number;
    videoType: FileUploadTypeEnum;
    channelNumber?: number;
    hideControls?: boolean;
    forFullscreenView?: boolean;
    isSmallView?: boolean;
    downloadable?: boolean;
    fullscreenable?: boolean;
    // When rendered alongside a hidden twin (original/AI overlay) for the same channel, only the
    // active instance drives shared sync state and playback; the inactive one stays paused and inert.
    isActive?: boolean;

    onCloseFullScreen?: () => void;
    onOpenFullScreen?: () => void;
    onVideoDownloaded?: () => void;
    onPlaybackProgress?: (currentTime: number, duration: number, framesPerSecond?: number) => void;
    // When provided, receives a function that pauses the player and seeks it to the given media time.
    seekRef?: React.MutableRefObject<((time: number) => void) | null>;
    // When provided, receives a function that pauses the player.
    pauseRef?: React.MutableRefObject<(() => void) | null>;
}

const styles = require('./VideoPlayer.scss') as {
    vidstackContainer: string;
    exitFullscreenButton: string;
};

const VideoPlayer = (props: VideoPlayerProps) => {
    const playerRef = useRef<MediaPlayerInstance>(null);
    const sliderRef = useRef<TimeSliderInstance>(null);
    const isSeekingRef = useRef<boolean>(false);
    const isRestartingRef = useRef<boolean>(false);
    const wasPlayingBeforeSeekRef = useRef<boolean>(false);
    const isPlayingGlobalRef = useRef<boolean>(false);
    const containerRef = useRef<HTMLDivElement>(null);
    // Frame rate measured from the actual clip (see the requestVideoFrameCallback effect); null until known.
    const measuredFpsRef = useRef<number | null>(null);
    // Latest onPlaybackProgress, read from inside the long-lived rVFC callback; and whether rVFC is driving
    // progress (so the store-driven fallback can stand down).
    const onPlaybackProgressRef = useRef(props.onPlaybackProgress);
    const rvfcActiveRef = useRef(false);
    const remote = useMediaRemote(playerRef);
    const {
        isPlayingGlobal,
        setIsPlayingGlobal,
        maxDurationGlobal,
        setMaxDurationGlobal,
        maxDurationVideoKey,
        seekTimeGlobal,
        setSeekTimeGlobal,
        videosStarted,
        setVideosStarted,
        videosWatched,
        setVideosWatched,
        setVideoEnded,
        allVideosEnded,
        startPlayFromBeginning,
        setStartPlayFromBeginning,
    } = useVideoPlayerSync();

    const { paused, ended, currentTime, duration } = useMediaStore(playerRef);

    const isActive = props.isActive ?? true;
    // Only a player rendered as one half of an active/inactive pair (see VideoBox) needs the custom
    // load-control below — a plain, always-active player should keep Vidstack's own default loading
    // strategy (e.g. lazy-load until visible), exactly as it did before this existed.
    const isDualInstance = props.isActive !== undefined;
    const videoKey = props.channelNumber != null ? String(props.channelNumber) : props.videoType;

    const isLocallyAtEndZone = (time: number): boolean => time >= Math.trunc(duration);
    const isGloballyAtEndZone = (time: number): boolean => time >= Math.trunc(maxDurationGlobal);
    const isGloballyAtEndWithTolerance = (time: number): boolean => time >= Math.trunc(maxDurationGlobal) - 0.15;

    useEffect(() => {
        isPlayingGlobalRef.current = isPlayingGlobal;
    }, [isPlayingGlobal]);

    useEffect(() => {
        if (!isActive) return;

        const player = playerRef.current;
        if (!player || duration === 0) return;

        setMaxDurationGlobal(duration, videoKey);

        return () => {
            if (!player || props.forFullscreenView) return;
            setSeekTimeGlobal(player.state.ended ? maxDurationGlobal : player.currentTime);
        };
    }, [duration, isActive]);

    useEffect(() => {
        if (!isActive || !ended) return;
        setVideoEnded(videoKey, ended);
    }, [ended, videoKey, setVideoEnded, isActive]);

    // Keep the latest progress callback reachable from the long-lived rVFC callback below.
    useEffect(() => {
        onPlaybackProgressRef.current = props.onPlaybackProgress;
    });

    // Expose imperative pause and pause+seek to the parent (used by the recall preview to scrub the video
    // to a typed time). Pausing before seeking keeps the sought frame on screen.
    useEffect(() => {
        const seekRef = props.seekRef;
        const pauseRef = props.pauseRef;
        if (seekRef) {
            seekRef.current = (time: number) => {
                const player = playerRef.current;
                if (!player) return;

                // The player MUST be paused before seeking. Seeking while it is still playing desyncs
                // vidstack's state: playback stops but the controls keep showing the playing state and the
                // player stops responding. remote.pause() only enqueues a request and returns before the
                // player has actually paused, so the pause is awaited here instead -- player.pause()
                // resolves immediately when it is already paused, making this safe in every state.
                player
                    .pause(new Event('seekSyncPause'))
                    .then(() => {
                        // Seeking while the player is ended cannot work directly: vidstack pins the store's
                        // currentTime to the duration while `ended` is set, and its seeked handler
                        // re-derives "still at the end" from that pinned value, so `ended` can never clear
                        // itself -- the slider stays stuck even though the media element (the frame) seeks
                        // fine. Only the play handler clears `ended`, so a play/pause nudge is required
                        // (same workaround as the seekTimeGlobal sync effect below). That play handler ALSO
                        // resets playback to the start when it sees `ended`, which vidstack dispatches
                        // internally and cannot be prevented -- so the target seek is issued the moment
                        // play() resolves, superseding the reset-to-0 before it can visibly settle, with a
                        // delayed re-seek to land the exact frame once the dance is over. The direct
                        // element seek up front just shows the target frame ASAP.
                        if (player.state.ended || !player.state.started) {
                            isRestartingRef.current = true;
                            const video = containerRef.current?.querySelector('video');
                            if (video) video.currentTime = time;
                            return player
                                .play(new Event('seekSyncPlay'))
                                .then(() => {
                                    remote.seek(time);
                                    return player.pause(new Event('seekSyncPause'));
                                })
                                .then(() => {
                                    setTimeout(() => {
                                        remote.seek(time);
                                        isRestartingRef.current = false;
                                    }, 100);
                                });
                        }

                        remote.seek(time);
                        return undefined;
                    })
                    .catch(() => {
                        isRestartingRef.current = false;
                        remote.seek(time);
                    });
            };
        }
        if (pauseRef) {
            // Awaited by callers that need the pause to have landed; see seekRef above.
            pauseRef.current = () => playerRef.current?.pause(new Event('previewPause')).catch(() => {});
        }
        return () => {
            if (seekRef) seekRef.current = null;
            if (pauseRef) pauseRef.current = null;
        };
    }, [remote]);

    // Fallback progress reporting driven by the media store's currentTime -- NOT aligned to frame
    // presentation, so it can drift from the visible frame. Used only when frame-accurate reporting via
    // requestVideoFrameCallback isn't active (e.g. unsupported browser); rVFC stands this down when it is.
    useEffect(() => {
        if (rvfcActiveRef.current) return;
        if (!props.onPlaybackProgress || duration === 0) return;
        props.onPlaybackProgress(currentTime, duration, measuredFpsRef.current ?? undefined);
    }, [currentTime, duration]);

    // Frame-accurate progress + frame-rate measurement via requestVideoFrameCallback. The callback fires
    // exactly when a new frame is presented (playback OR scrub) and carries that frame's true mediaTime, so
    // we report progress in lockstep with the visible frame -- the computed time changes when the frame does,
    // never mid-frame. The smallest positive gap between consecutive frames' mediaTime is one frame's
    // duration (robust to dropped frames), giving the exact fps. If the browser lacks rVFC, this stays
    // inactive and the store-driven fallback above reports instead.
    useEffect(() => {
        measuredFpsRef.current = null;
        rvfcActiveRef.current = false;
        if (!props.onPlaybackProgress) return; // only consumers that use progress need this
        const container = containerRef.current;
        if (!container) return;

        let cancelled = false;
        let rafId = 0;
        let rvfcHandle = 0;
        let video: HTMLVideoElement | null = null;
        let prevMediaTime: number | null = null;
        let minFrameDelta = Infinity;
        let attempts = 0;

        const onFrame = (_now: number, metadata: { mediaTime: number }) => {
            if (cancelled || !video) return;
            const t = metadata.mediaTime;
            if (prevMediaTime !== null) {
                const delta = t - prevMediaTime;
                if (delta > 0 && delta < minFrameDelta) {
                    minFrameDelta = delta;
                    measuredFpsRef.current = 1 / minFrameDelta;
                }
            }
            prevMediaTime = t;

            const dur = video.duration;
            if (dur > 0 && isFinite(dur)) {
                onPlaybackProgressRef.current?.(t, dur, measuredFpsRef.current ?? undefined);
            }
            rvfcHandle = (video as any).requestVideoFrameCallback(onFrame);
        };

        // The <video> element is created by the provider shortly after mount; retry briefly until it exists.
        const attach = () => {
            if (cancelled) return;
            video = container.querySelector('video');
            if (!video) {
                if (attempts++ < 120) rafId = requestAnimationFrame(attach);
                return;
            }
            if (typeof (video as any).requestVideoFrameCallback !== 'function') return; // unsupported -> fallback stays
            rvfcActiveRef.current = true;
            rvfcHandle = (video as any).requestVideoFrameCallback(onFrame);
        };
        attach();

        return () => {
            cancelled = true;
            rvfcActiveRef.current = false;
            if (rafId) cancelAnimationFrame(rafId);
            if (rvfcHandle && video && typeof (video as any).cancelVideoFrameCallback === 'function') {
                (video as any).cancelVideoFrameCallback(rvfcHandle);
            }
        };
    }, [props.src]);

    // ------------ Sync Local -> Global ----------------
    const handleOnPlay = (nativeEvent: MediaPlayEvent) => {
        if (!isActive) return;
        if (nativeEvent.isOriginTrusted) {
            if (isLocallyAtEndZone(currentTime + 0.15)) {
                remote.seek(0);
                setStartPlayFromBeginning(true);
            }
            setIsPlayingGlobal(true);
        }
        // Unlike videosWatched below, "started" isn't scoped to a specific channel -- any channel
        // beginning playback means the user has started viewing the event, which is what drives the
        // New -> In_Progress workflow status update. Scoping this to maxDurationVideoKey (a channel
        // chosen by which video's metadata happens to load first, in multi-channel recalls) meant
        // playing any other channel first silently never triggered the status update.
        if (!videosStarted) {
            setVideosStarted(true);
        }
    };

    const handleOnPause = (nativeEvent: MediaPauseEvent) => {
        if (!isActive) return;
        let isGloballyAtEnd = false;
        if (nativeEvent.isOriginTrusted) {
            if (startPlayFromBeginning) setStartPlayFromBeginning(false);

            // check if we have reached the end of this specific video and is the same time as the maxDuration, if so then set the isPlaying to false
            const isLocallyEnded = ended || isLocallyAtEndZone(currentTime);
            isGloballyAtEnd = isGloballyAtEndZone(currentTime);

            if (isLocallyEnded && isGloballyAtEnd) {
                setIsPlayingGlobal(false);
            } else if (!isLocallyEnded) {
                setIsPlayingGlobal(false);
            }
        }

        if (!videosWatched && isGloballyAtEndWithTolerance(currentTime)) {
            if (videoKey === maxDurationVideoKey || props.forFullscreenView) {
                setVideosWatched(true);
            }
        }
    };

    const handleOnMediaSeekingRequest = (seekTime: number) => {
        const player = playerRef.current;
        if (!player) return;

        const isSeekingToEndZone = isLocallyAtEndZone(seekTime);
        const localSeekTime = isSeekingToEndZone ? duration : seekTime;
        const globalSeekTime = isSeekingToEndZone ? maxDurationGlobal : seekTime;

        if (player.paused && !isRestartingRef.current) {
            if (!isSeekingToEndZone) {
                if (player.state.ended) {
                    player
                        .play(new Event('seekSyncPlay'))
                        .then(() => player.pause(new Event('seekSyncPause')))
                        .catch(() => {});
                } else {
                    remote.seek(localSeekTime);
                    setSeekTimeGlobal(globalSeekTime);
                }
            }
        }
    };

    const handleSliderOnMediaPlayRequest = (nativeEvent: MediaPlayRequestEvent) => {
        const slider = sliderRef.current;
        if (!slider) return;

        const sliderTime = slider.state.pointerRate * duration;

        if (isLocallyAtEndZone(sliderTime + 0.15)) {
            nativeEvent.preventDefault();
        }
    };

    const handleOnSeeked = (seekTime: number, nativeEvent: MediaSeekedEvent) => {
        if (nativeEvent.isOriginTrusted && !isSeekingRef.current) {
            setSeekTimeGlobal(seekTime);
        }
    };

    const onDragStart = () => {
        if (!paused) {
            remote.pause();
            setIsPlayingGlobal(false);
            wasPlayingBeforeSeekRef.current = true;
        }
        isSeekingRef.current = true;
    };

    const onDragEnd = () => {
        isSeekingRef.current = false;
        if (wasPlayingBeforeSeekRef.current) {
            if (paused) {
                remote.play();
            }
            setIsPlayingGlobal(true);
            wasPlayingBeforeSeekRef.current = false;
        }
    };

    // ------------- Sync Global -> Local ---------------
    useEffect(() => {
        if (!isActive || isSeekingRef.current || props.forFullscreenView) return;

        const player = playerRef.current;
        if (!player) return;

        if (paused && isPlayingGlobal) {
            if (startPlayFromBeginning) {
                remote.seek(0);
                setStartPlayFromBeginning(false);
            }

            if (isLocallyAtEndZone(player.currentTime) && !allVideosEnded() && !startPlayFromBeginning) {
                return;
            }

            remote.play();
        }

        if (!paused && !isPlayingGlobal) {
            remote.pause();
        }
    }, [isPlayingGlobal, startPlayFromBeginning, isActive]);

    useEffect(() => {
        if (!isActive || isSeekingRef.current || props.forFullscreenView) return;

        const player = playerRef.current;
        if (!player) return;

        if (isRestartingRef.current) return;

        if (Math.abs(player.currentTime - seekTimeGlobal) < 0.3) return;

        const isSeekingToEndZone = isLocallyAtEndZone(seekTimeGlobal);

        if (player.state.ended && !isSeekingToEndZone) {
            isRestartingRef.current = true;
            player
                .play(new Event('seekSyncPlay'))
                .then(() => player.pause(new Event('seekSyncPause')))
                .then(() => {
                    setTimeout(() => {
                        remote.seek(seekTimeGlobal);
                        isRestartingRef.current = false;
                        // The dance above always ends paused — resume if the rest of the channels are playing,
                        // otherwise this channel is left stuck paused even though playback should continue.
                        // Read the ref rather than depend on isPlayingGlobal directly: adding it to this
                        // effect's deps would re-run the whole seek-correction effect on every play/pause
                        // toggle, not just when seekTimeGlobal changes, racing with the other sync effect.
                        if (isPlayingGlobalRef.current) {
                            remote.play();
                        }
                    }, 100);
                })
                .catch(() => {
                    isRestartingRef.current = false;
                });
        } else {
            remote.seek(seekTimeGlobal);
        }
    }, [seekTimeGlobal, isActive]);

    // Keeps the shared timeline fresh while this is the active instance, so the inactive twin
    // (original/AI overlay for the same channel) can pick up the exact position when it becomes active.
    // Only relevant for a dual-instance pair — a plain single player doesn't need this extra churn.
    useEffect(() => {
        if (!isDualInstance || !isActive) return;
        if (Math.abs(currentTime - seekTimeGlobal) < 0.5) return;
        setSeekTimeGlobal(currentTime);
    }, [currentTime, isActive, isDualInstance]);

    // On becoming active, immediately catch up to the shared position/play-state rather than
    // waiting for the effects above to react to some other value changing. `load="custom"` (below)
    // means this instance may not have started loading at all yet (e.g. it was hidden), so kick that
    // off explicitly rather than relying on Vidstack's default visibility-based load strategy, which
    // races with the CSS visibility change and can lose the seek if loading starts after we call it.
    // Only relevant for a dual-instance pair — a plain single player already loads/syncs on mount
    // via the effects above, and doesn't need `startLoading()` since it never used `load="custom"`.
    useEffect(() => {
        if (!isDualInstance || !isActive) return;

        const player = playerRef.current;
        if (!player) return;

        player.startLoading();
        remote.seek(seekTimeGlobal);

        if (isPlayingGlobal) {
            remote.play();
        } else {
            remote.pause();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive, isDualInstance]);

    // On becoming inactive, stop playback so a hidden twin doesn't keep decoding/streaming in the background.
    useEffect(() => {
        if (isActive) return;
        remote.pause();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    return (
        <div ref={containerRef} className="vidstack-plyr" style={props.isSmallView ? { position: 'relative' } : undefined}>
            {props.isSmallView && paused && <SmallViewPlayOverlay />}
            <Box
                sx={{
                    maxHeight: props.maxHeight ?? 'auto',
                    maxWidth: '100%',
                    '& [data-media-player]': {
                        maxHeight: props.maxHeight ?? 'auto',
                        maxWidth: '100%',
                    },
                    '& [data-media-player] video': {
                        maxHeight: props.maxHeight ?? 'auto',
                        maxWidth: '100%',
                    },
                    '& [data-media-player][data-layout="plyr"]:not([data-fullscreen])': {
                        borderRadius: '0px !important',
                        maxHeight: props.maxHeight ?? 'auto',
                        maxWidth: '100%',
                    },
                }}
                className={styles.vidstackContainer}
                data-hide-controls={props.hideControls ?? false}
                data-fullscreen={props.forFullscreenView ?? false}
                data-small-view={props.isSmallView ?? false}
            >
                <MediaPlayer
                    ref={playerRef}
                    src={props.src}
                    title={props.title}
                    muted
                    loop={false}
                    autoPlay={isActive && isPlayingGlobal}
                    preload={isDualInstance ? (isActive ? 'auto' : 'none') : undefined}
                    load={isDualInstance ? 'custom' : undefined}
                    onPlay={handleOnPlay}
                    onPause={handleOnPause}
                    onSeeked={handleOnSeeked}
                >
                    <MediaProvider
                        mediaProps={{
                            // @ts-expect-error: attribute not part of HTMLMediaElement but supported by <video></video>
                            controlsList: 'nodownload nofullscreen noremoteplayback',
                            disablePictureInPicture: true,
                            disableRemotePlayback: true,
                            onContextMenuCapture: (e: any) => e.preventDefault(),
                        }}
                    />
                    <PlyrLayout
                        clickToFullscreen={false}
                        icons={plyrLayoutIcons}
                        controls={
                            props.hideControls
                                ? ['play-large']
                                : ['play-large', 'play', 'progress', 'current-time', 'duration', 'download', 'fullscreen']
                        }
                        invertTime={false}
                        slots={{
                            download: props.downloadable !== false && (
                                <VideoDownloadControl
                                    videoUrl={props.src}
                                    videoType={props.videoType}
                                    channelNumber={props.channelNumber}
                                    onVideoDownloaded={props.onVideoDownloaded}
                                ></VideoDownloadControl>
                            ),
                            fullscreenButton:
                                (props.forFullscreenView && !props.isSmallView) || props.fullscreenable === true ? (
                                    <FontAwesomeIcon
                                        className={styles.exitFullscreenButton}
                                        icon={!props.forFullscreenView ? faExpand : faCompress}
                                        title={!props.forFullscreenView ? 'Enter Fullscreen' : 'Exit Fullscreen'}
                                        onClick={() => {
                                            !props.forFullscreenView
                                                ? props.onOpenFullScreen && props.onOpenFullScreen()
                                                : props.onCloseFullScreen && props.onCloseFullScreen();
                                            remote.pause();
                                        }}
                                    />
                                ) : null,
                            timeSlider: (
                                <VideoTimeSliderControl
                                    ref={sliderRef}
                                    pauseWhileDragging={false}
                                    seekingRequestThrottle={100}
                                    onDragStart={onDragStart}
                                    onDragEnd={onDragEnd}
                                    onMediaSeekingRequest={handleOnMediaSeekingRequest}
                                    onMediaPlayRequest={handleSliderOnMediaPlayRequest}
                                />
                            ),
                        }}
                    />
                </MediaPlayer>
            </Box>
        </div>
    );
};

export default VideoPlayer;
